import { Dispatch, SetStateAction, useMemo } from 'react';
import { css } from '@emotion/react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { uniqBy } from 'lodash-es';
import { theme } from '@frontend/theme';
import { HintScroll } from '@frontend/design-system';
import { useGetAppointmentTypesV3 } from '../../../../../../hooks/scheduler-v3/use-get-calendar-v3-queries-data';
import { useScheduleActionsContext } from '../../ScheduleActionsContext';
import { ScheduleActionsProvider } from '../../ScheduleActionsProvider';
import { ScheduleRequestContextProvider } from '../Context/ScheduleRequestContext';
import { useScheduleRequestContextValue } from '../hooks/use-schedule-request-context-value';
import { useScheduleRequestModalContextValue } from '../hooks/use-schedule-request-modal-context-value';
import { ScheduleRequestTray } from '../ScheduleRequestModal/ScheduleRequestWrappers/ScheduleRequestTray';
import { ScheduleRequestOverlappingListItem } from './schedule-request-overlapping-list-item/schedule-request-overlapping-list-item';
import { ScheduleRequestFormManager } from './ScheduleRequestFormManager';

type ScheduleRequestListContentProps = {
  scheduleRequests: ScheduleRequest[];
  className?: string;
};

const WrappedScheduleRequestList = ({ scheduleRequests, className }: ScheduleRequestListContentProps) => {
  const { selectedScheduleRequest, setSelectedScheduleRequest, scheduleRequestModalContextValue } =
    useScheduleRequestModalContextValue({ refetchScheduleRequestList: () => {} });
  const { scheduleRequestModalProps } = scheduleRequestModalContextValue;
  const scheduleActionsContextValue = useScheduleActionsContext();
  const locationIds = useMemo(
    () =>
      uniqBy(scheduleRequests, 'locationId')
        .map((request) => request.locationId)
        .filter(Boolean) as string[],
    [scheduleRequests]
  );

  const { data: appointmentTypesData } = useGetAppointmentTypesV3(locationIds);

  const { providerValue } = useScheduleRequestContextValue({ refetchScheduleRequestList: () => {} });

  return (
    <>
      <ScheduleRequestContextProvider value={providerValue}>
        <HintScroll className={className} css={styles.listContainer}>
          {scheduleRequests.map((listItem) => (
            <ScheduleRequestOverlappingListItem
              key={listItem.id}
              listItem={listItem}
              setSelectedScheduleRequest={
                setSelectedScheduleRequest as Dispatch<SetStateAction<ScheduleRequest | null>>
              }
              scheduleRequestModalProps={scheduleRequestModalProps}
              appointmentTypes={appointmentTypesData?.appointmentTypes || []}
            />
          ))}
        </HintScroll>
      </ScheduleRequestContextProvider>
      {selectedScheduleRequest && (
        <ScheduleRequestFormManager
          wrapper={ScheduleRequestTray}
          scheduleRequestModalContextValue={scheduleRequestModalContextValue}
          selectedScheduleRequest={selectedScheduleRequest}
          scheduleActionsContextValue={scheduleActionsContextValue}
        />
      )}
    </>
  );
};

export const ScheduleRequestList = (props: ScheduleRequestListContentProps) => {
  return (
    <ScheduleActionsProvider>
      <WrappedScheduleRequestList {...props} />
    </ScheduleActionsProvider>
  );
};

const styles = {
  listContainer: css`
    background-color: ${theme.colors.neutral5};
    overflow-y: auto;
  `,
};
